// The one shape every adapter maps its source into, so the UI (and the agent)
// only ever reason about a single event type.

export const SOURCE = {
  EVENT: 'event',
  HEARTBEAT: 'heartbeat',
  DRIVER: 'driver',
  SNAPSHOT: 'snapshot',
};

// How a record is READ, which is a different question from what it means. A beat is
// a moment you read on its own — a restock, a vend. A series point is one sample of
// a curve that means nothing without its neighbours. Declared per type so the split
// happens once, in buildTimeline, and no consumer has to name a type to do it.
export const CHANNEL = {
  BEAT: 'beat',
  SERIES: 'series',
};

export const SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
};

// type -> presentation. `category` drives the filter chips; `severity` drives color.
export const TYPE_META = {
  PACKING_SLIP_SCAN: {
    label: 'Packing slip scanned',
    category: 'driver',
    severity: SEVERITY.INFO,
    icon: '📋',
  },
  DRIVER_FIX: {
    label: 'Driver fix',
    category: 'driver',
    severity: SEVERITY.WARNING,
    icon: '🔧',
  },
  LOCATION_STOCKED: {
    label: 'Location stocked',
    category: 'driver',
    severity: SEVERITY.INFO,
    icon: '📦',
  },
  WASTED_ITEMS: {
    label: 'Wasted items',
    category: 'driver',
    severity: SEVERITY.INFO,
    icon: '🗑️',
  },
  LOCATION_UNREACHABLE: {
    label: 'Location unreachable',
    category: 'driver',
    severity: SEVERITY.WARNING,
    icon: '📡',
  },
  CLEAR_OUT_DECOMMISSION: {
    label: 'Clear-out / decommission',
    category: 'driver',
    severity: SEVERITY.WARNING,
    icon: '🧹',
  },
  DOOR_OPEN: {
    label: 'Door opened',
    category: 'door',
    severity: SEVERITY.INFO,
    icon: '🚪',
  },
  DOOR_CLOSE: {
    label: 'Door closed',
    category: 'door',
    severity: SEVERITY.INFO,
    icon: '🚪',
  },
  SAFETY_HOLD: {
    label: 'Safety hold tripped',
    category: 'status',
    severity: SEVERITY.CRITICAL,
    icon: '🛑',
  },
  OUT_OF_ORDER: {
    label: 'Marked out of order',
    category: 'status',
    severity: SEVERITY.CRITICAL,
    icon: '⛔',
  },
  TEMP: {
    label: 'Temperature reported',
    category: 'temp',
    channel: CHANNEL.SERIES,
    severity: SEVERITY.INFO,
    icon: '🌡️',
  },
  VEND: {
    label: 'Vend',
    category: 'vend',
    severity: SEVERITY.INFO,
    icon: '🛒',
  },
  VEND_KIOSK: {
    label: 'Kiosk purchase',
    category: 'vend',
    severity: SEVERITY.INFO,
    icon: '🧊',
  },
  VEND_MOBILE: {
    label: 'Mobile pickup',
    category: 'vend',
    severity: SEVERITY.INFO,
    icon: '📱',
  },
  VEND_FAILED: {
    label: 'Vend failed',
    category: 'vend',
    severity: SEVERITY.WARNING,
    icon: '⚠️',
  },
  VEND_ITEM: {
    label: 'Item dispensed',
    category: 'vend',
    severity: SEVERITY.INFO,
    icon: '📤',
  },
  VEND_RETRY: {
    label: 'Vend retry',
    category: 'vend',
    severity: SEVERITY.WARNING,
    icon: '🔁',
  },
  EMPTY_CHANNEL: {
    label: 'Empty channel reset',
    category: 'vend',
    severity: SEVERITY.WARNING,
    icon: '🕳️',
  },
  AUTH: {
    label: 'Card authorization',
    category: 'payment',
    severity: SEVERITY.INFO,
    icon: '🔐',
  },
  CAPTURE: {
    label: 'Payment capture',
    category: 'payment',
    severity: SEVERITY.INFO,
    icon: '🧾',
  },
  COUPON: {
    label: 'Coupon applied',
    category: 'payment',
    severity: SEVERITY.INFO,
    icon: '🎟️',
  },
  RESERVATION: {
    label: 'Reservation created',
    category: 'reservation',
    severity: SEVERITY.INFO,
    icon: '📅',
  },
  RESERVATION_REDEEM: {
    label: 'Reservation redeemed',
    category: 'reservation',
    severity: SEVERITY.INFO,
    icon: '✅',
  },
  RESERVATION_NOSHOW: {
    label: 'Reservation no-show',
    category: 'reservation',
    severity: SEVERITY.WARNING,
    icon: '🚫',
  },
  RESERVATION_CANCELED: {
    label: 'Reservation canceled',
    category: 'reservation',
    severity: SEVERITY.INFO,
    icon: '❌',
  },
  WORK_ORDER: {
    label: 'Work order',
    category: 'maintenance',
    severity: SEVERITY.WARNING,
    icon: '🛠️',
  },
  EMAINT: {
    label: 'eMaint ticket',
    category: 'maintenance',
    severity: SEVERITY.WARNING,
    icon: '🎫',
  },
  SWIPE: {
    label: 'Card swipe',
    category: 'swipe',
    severity: SEVERITY.INFO,
    icon: '💳',
  },
  INVENTORY: {
    label: 'Inventory change',
    category: 'inventory',
    severity: SEVERITY.INFO,
    icon: '📊',
  },
  CHECKPOINT: {
    label: 'Heartbeat checkpoint',
    category: 'system',
    severity: SEVERITY.INFO,
    icon: '•',
  },
  SEGMENT: {
    label: 'UI interaction',
    category: 'ui',
    severity: SEVERITY.INFO,
    icon: '👆',
  },
};

// Cheap stable digest — only has to tell two distinct events apart.
function digest(value) {
  let str;
  try {
    str = JSON.stringify(value) || '';
  } catch (e) {
    str = String(value); // circular or otherwise unserializable
  }
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % 2147483647;
  }
  return h.toString(36);
}

// Presentation comes from TYPE_META, so adapters supply only ts + type + text.
// `value` is the reading a series point carries; a beat has none. Adapters that
// already parse a number out of the payload pass it here rather than dropping it
// into a title string and leaving the chart to parse it a second time.
export function makeEvent({
  ts,
  source,
  type,
  title,
  summary,
  raw,
  severity,
  value,
}) {
  const meta = TYPE_META[type] || {
    label: type,
    category: 'other',
    severity: SEVERITY.INFO,
    icon: '•',
  };
  return {
    // Digest is in the id on purpose: buildTimeline dedupes by id, and
    // source+type+ts collides for two real events in the same second.
    id: `${source}:${type}:${ts}:${digest(raw)}`,
    ts,
    source,
    type,
    category: meta.category,
    channel: meta.channel || CHANNEL.BEAT,
    value: value == null ? null : value,
    severity: severity || meta.severity, // optional per-event override (e.g. failed auth)
    icon: meta.icon,
    title: title || meta.label,
    summary: summary || '',
    raw,
  };
}
